// "Layers" rail panel: every section and element as a tree. It is the keyboard
// path to everything on the page — each row is a button (Tab / Enter) with
// rename, lock, hide and move up/down.

import React, { useState } from "react";
import FeatherIcon from "feather-icons-react";
import { IconButton } from "../controls";
import { ELEMENT_ICONS, elementLabel } from "../designModel";

function ElementRow({ el, depth, selection, actions, index, count }) {
  const [renaming, setRenaming] = useState(false);
  const selected = selection?.kind === "element" && selection.id === el.id;
  return (
    <li>
      <div className={`td-layer${selected ? " is-selected" : ""}${el.hidden ? " is-hidden" : ""}`} style={{ paddingLeft: 8 + depth * 14 }}>
        <FeatherIcon icon={ELEMENT_ICONS[el.type] || "box"} size={13} className="td-layer__icon" />
        {renaming ? (
          <input
            className="td-input td-layer__rename"
            autoFocus
            defaultValue={el.name || elementLabel(el)}
            aria-label="Layer name"
            onBlur={(e) => {
              actions.updateElement(el.id, { name: e.target.value.trim() || undefined });
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setRenaming(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="td-layer__name"
            aria-current={selected || undefined}
            onClick={() => actions.select({ kind: "element", id: el.id })}
            onDoubleClick={() => setRenaming(true)}
            title="Click to select, double-click to rename"
          >
            {elementLabel(el)}
          </button>
        )}
        <div className="td-layer__tools">
          <IconButton icon="edit-2" label="Rename" size={12} onClick={() => setRenaming(true)} />
          <IconButton icon={el.locked ? "lock" : "unlock"} label={el.locked ? "Unlock" : "Lock position"} size={12} active={!!el.locked} onClick={() => actions.updateElement(el.id, { locked: el.locked ? undefined : true })} />
          <IconButton icon={el.hidden ? "eye-off" : "eye"} label={el.hidden ? "Show" : "Hide"} size={12} active={!!el.hidden} onClick={() => actions.updateElement(el.id, { hidden: el.hidden ? undefined : true })} />
          <IconButton icon="chevron-up" label="Move up" size={12} disabled={index === 0} onClick={() => actions.shiftElement(el.id, -1)} />
          <IconButton icon="chevron-down" label="Move down" size={12} disabled={index === count - 1} onClick={() => actions.shiftElement(el.id, 1)} />
        </div>
      </div>
      {el.type === "row" && (
        <ul className="td-layers__sub">
          {el.columns.map((c, ci) => (
            <li key={c.id}>
              <div className="td-layer td-layer--col" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>
                <FeatherIcon icon="sidebar" size={12} className="td-layer__icon" />
                <span className="td-layer__name td-layer__name--static">Column {ci + 1}</span>
              </div>
              <ul className="td-layers__sub">
                {c.elements.map((child, k) => (
                  <ElementRow key={child.id} el={child} depth={depth + 2} selection={selection} actions={actions} index={k} count={c.elements.length} />
                ))}
                {!c.elements.length && <li className="td-layer td-layer--empty" style={{ paddingLeft: 8 + (depth + 2) * 14 }}>Empty</li>}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function LayersPanel({ design, selection, actions }) {
  const [collapsed, setCollapsed] = useState({});
  return (
    <div className="td-panel">
      <p className="td-hint">Everything on the page, top to bottom. Use Tab and Enter to reach anything with the keyboard.</p>
      <ul className="td-layers" aria-label="Layers">
        {design.bands.map((band, bi) => {
          const selected = selection?.kind === "band" && selection.id === band.id;
          const open = !collapsed[band.id];
          return (
            <li key={band.id}>
              <div className={`td-layer td-layer--band${selected ? " is-selected" : ""}`}>
                <IconButton
                  icon={open ? "chevron-down" : "chevron-right"}
                  label={open ? "Collapse" : "Expand"}
                  size={12}
                  onClick={() => setCollapsed((c) => ({ ...c, [band.id]: open }))}
                />
                <button type="button" className="td-layer__name" aria-current={selected || undefined} onClick={() => actions.select({ kind: "band", id: band.id })}>
                  {band.label}
                </button>
                <span className="td-badge">{band.kind === "free" ? "Free" : "Flow"}</span>
                {band.pin !== "none" && <FeatherIcon icon="repeat" size={12} aria-label={`Repeats at the ${band.pin} of every page`} />}
                <div className="td-layer__tools">
                  <IconButton icon="chevron-up" label="Move section up" size={12} disabled={bi === 0} onClick={() => actions.shiftBand(band.id, -1)} />
                  <IconButton icon="chevron-down" label="Move section down" size={12} disabled={bi === design.bands.length - 1} onClick={() => actions.shiftBand(band.id, 1)} />
                </div>
              </div>
              {open && (
                <ul className="td-layers__sub">
                  {band.elements.map((el, i) => (
                    <ElementRow key={el.id} el={el} depth={1} selection={selection} actions={actions} index={i} count={band.elements.length} />
                  ))}
                  {!band.elements.length && <li className="td-layer td-layer--empty" style={{ paddingLeft: 22 }}>Empty section</li>}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
